import { useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { logMessage } from '@modernloop/shared/utils';
import { useSnackbar } from 'notistack';

import {
  type SwitchActiveOrganizationMutation_switchActiveOrganization,
  useSwitchActiveOrganizationMutation,
} from 'src/generated/mloop-graphql';

import { getAuth0Client } from 'src/contexts/auth0/utils';

import { Routes } from 'src/constants/routes';

import { clearOrganizationScopedStorage } from 'src/utils/clearOrganizationScopedStorage';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SwitchActiveOrganization = gql`
  mutation SwitchActiveOrganization($input: SwitchActiveOrganizationInput!) {
    switchActiveOrganization(input: $input) {
      __typename
      ... on SwitchActiveOrganizationSuccess {
        activeOrganizationId
      }
      ... on Error {
        errorCode
        errorMessage
      }
    }
  }
`;

/** What the switcher needs to render and drive one Organization switch. */
export type SwitchActiveOrganizationHandle = {
  /** The Organization a switch is in flight for, or null while none is. */
  readonly switchingOrganizationId: string | null;
  /** Moves the acting user into `organizationId` and reloads the app there. */
  readonly switchToOrganization: (organizationId: string) => Promise<void>;
};

/**
 * Runs the whole Organization switch: the mutation, a fresh Auth0 id token, the storage reset, and
 * the reload.
 *
 * The steps are ordered and each one depends on the one before it, so they live in one hook rather
 * than in the component that renders the menu.
 */
export const useSwitchActiveOrganization = (): SwitchActiveOrganizationHandle => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [switchActiveOrganization] = useSwitchActiveOrganizationMutation();
  const [switchingOrganizationId, setSwitchingOrganizationId] = useState<string | null>(null);
  // A ref, not the state above: a second click can land before React re-renders the disabled rows.
  const switchingRef = useRef(false);

  const showGenericFailure = (): void => {
    enqueueSnackbar(t('hiring-dashboard-nav:nav.organizationSwitcher.switchFailed'), { variant: 'error' });
  };

  /**
   * Re-mints the id token and leaves the single-page app.
   *
   * `getTokenSilently({ ignoreCache: true })` makes Auth0 call `/org/user/login` again, so the
   * `user_info` claim carries the Organization the mutation just made active.
   *
   * The reload is the reset, on purpose. It re-creates the Apollo and react-query module
   * singletons, re-identifies LaunchDarkly, Datadog and Amplitude, and drops any deep link that
   * names a row of the Organization the user just left. A soft navigation or `client.clearStore()`
   * would leave the other three untouched.
   */
  const reloadIntoActiveOrganization = async (): Promise<void> => {
    try {
      const auth0Client = await getAuth0Client();
      await auth0Client.getTokenSilently({ ignoreCache: true });
    } catch (error) {
      // The mutation already moved the Active Organization on the server, so the reload happens
      // either way. `AuthProvider` asks for a token again while it boots, and the claim catches up
      // there. Leaving the user on a page that still names the Organization they left is worse.
      logMessage('Could not re-mint the id token after an Organization switch', 'error', error);
    }
    clearOrganizationScopedStorage();
    window.location.assign(Routes.MySchedulingTasks);
  };

  const applyOutput = async (
    output: SwitchActiveOrganizationMutation_switchActiveOrganization | undefined
  ): Promise<void> => {
    switch (output?.__typename) {
      case 'SwitchActiveOrganizationSuccess':
        return reloadIntoActiveOrganization();
      case 'SwitchActiveOrganizationNotAMemberError':
      case 'SwitchActiveOrganizationNotAllowedWhileImpersonatingError':
      case 'UnknownError':
        // The message is already localized by the backend, so it is rendered, never re-derived
        // from `errorCode` here.
        enqueueSnackbar(output.errorMessage, { variant: 'error' });
        return;
      case undefined:
        showGenericFailure();
        return;
      default: {
        const exhaustiveOutput: never = output;
        return exhaustiveOutput;
      }
    }
  };

  const switchToOrganization = async (organizationId: string): Promise<void> => {
    if (switchingRef.current) {
      return;
    }
    switchingRef.current = true;
    setSwitchingOrganizationId(organizationId);
    try {
      const response = await switchActiveOrganization({ variables: { input: { organizationId } } });
      await applyOutput(response.data?.switchActiveOrganization);
    } catch {
      showGenericFailure();
    } finally {
      switchingRef.current = false;
      setSwitchingOrganizationId(null);
    }
  };

  return { switchingOrganizationId, switchToOrganization };
};
